import { Injectable, computed, inject, linkedSignal, resource, signal } from '@angular/core';
import { Observable, firstValueFrom, map } from 'rxjs';
import { ConnectionService } from '../../shared/services/connection/connection.service';
import { ObjectStorageService } from '../../shared/services/object-storage/object-storage.service';
import { OAuthStorageService } from '../../shared/services/oauth-storage/oauth-storage.service';
import { ConnectionTestService } from '../../shared/services/connection-test/connection-test.service';
import { SecretsResourceStore } from '../../shared/services/secrets/secrets-resource.store';
import { StorageGrantService } from '../../shared/services/storage-grant/storage-grant.service';
import type { StorageGrant } from '@app/shared/interfaces/storage-grant.interface';
import {
  OAuthFlowService,
  OAuthPopupClosedError,
} from '../../shared/services/oauth-flow/oauth-flow.service';
import type {
  BoxAppOption,
  Connection,
  GlobalServiceOption,
  ConnectionTestResult,
  OAuthCredential,
  OAuthProviderConfig,
} from '@app/shared/interfaces';
import type { ObjectStorageConnection } from '@app/shared/interfaces/object-storage.interface';
import { ObjectStorageProvider } from '@app/shared/interfaces/object-storage.interface';
import {
  type ConnectionBasics,
  type StandardConnectionKey,
  buildCreateConnectionRequest,
  buildObjectStorageCreateRequest,
  buildObjectStorageTestRequest,
  buildObjectStorageUpdateRequest,
  buildUpdateConnectionRequest,
} from './connection-form.mapper';

interface CredentialsContext {
  projectId: number;
  provider: ObjectStorageProvider;
}

type FormValue = Record<string, unknown>;

/** Result emitted on a successful save: the dialog closes with this. */
export interface ConnectionSaveResult {
  type: string;
  connection: Connection | ObjectStorageConnection;
}

/**
 * Outcome of an OAuth authorization attempt:
 *  - `credentialId` set → success.
 *  - `error` set → the provider denied authorization (caller shows it).
 *  - `cancelled` → the user closed the popup (caller stays silent).
 */
export interface AuthorizeResult {
  credentialId: number | null;
  error?: string;
  cancelled?: boolean;
}

const SETTLED_STATUSES: ReadonlySet<string> = new Set(['resolved', 'error', 'local']);

/**
 * Orchestration store for the add-connection dialog (component-provided).
 *
 * Owns everything EXCEPT the reactive FormGroups (those stay in the component):
 *  - init data as `resource()`-backed signals — global services, box apps, OAuth
 *    providers/credentials — replacing the five manual loaders and the
 *    `pendingInitLoads` counter. Secrets come from the shared
 *    {@link SecretsResourceStore}.
 *  - save/test orchestration, delegating to the pure mapper builders +
 *    `ConnectionService`/`ObjectStorageService`/{@link ConnectionTestService}.
 *  - the OAuth popup handshake via {@link OAuthFlowService}.
 *
 * Mirrors the signals/`resource()` shape of `ProjectsListStore`; provided at the
 * component so its lifetime matches the dialog.
 */
/**
 * The OAuth integration for each storage provider: seeded ids, fixed by
 * migration 00072 (google_oauth, aws_oauth, azure_oauth). Plain S3-compatible
 * storage has no OAuth integration (EHQ-487).
 */
const OAUTH_INTEGRATION_IDS: Partial<Record<ObjectStorageProvider, number>> = {
  [ObjectStorageProvider.GCS]: 10,
  [ObjectStorageProvider.AWS]: 11,
  [ObjectStorageProvider.Azure]: 12,
};

@Injectable()
export class ConnectionFormStore {
  readonly #connectionService = inject(ConnectionService);
  readonly #objectStorageService = inject(ObjectStorageService);
  readonly #oauthStorage = inject(OAuthStorageService);
  readonly #connectionTest = inject(ConnectionTestService);
  readonly #oauthFlow = inject(OAuthFlowService);
  readonly #secretsStore = inject(SecretsResourceStore);
  readonly #storageGrants = inject(StorageGrantService);

  // ── Inputs ──────────────────────────────────────────────────────────────
  readonly #projectId = signal<number | null>(null);
  /**
   * The global-service list is only needed for the Service connection tab's
   * dropdown, so it is fetched when that tab opens rather than on every dialog
   * open (EHQ-255).
   */
  readonly #wantGlobalServices = signal(false);
  /** The object-storage provider whose OAuth providers should be loaded. */
  readonly #osProvider = signal<ObjectStorageProvider | null>(null);
  /** Set to load OAuth credentials for a (project, provider); null = idle. */
  readonly #credentialsContext = signal<CredentialsContext | null>(null);
  /** The project's storage grants are only needed once Keyless is chosen (EHQ-427). */
  readonly #wantStorageGrants = signal(false);

  // ── Init-data resources ──────────────────────────────────────────────────
  // Each resource is idle until its param signal holds a value; `params`
  // returns `undefined` to gate that, so the generic includes `undefined`. The
  // loader only runs for a defined params (hence the non-null assertions).
  readonly #globalServicesResource = resource<GlobalServiceOption[], number | undefined>({
    params: () => {
      const projectId = this.#projectId();
      if (projectId == null || !this.#wantGlobalServices()) {
        return undefined;
      }
      return projectId;
    },
    loader: ({ params }) => firstValueFrom(this.#connectionService.getGlobalServices(params!)),
  });

  readonly #boxAppsResource = resource<BoxAppOption[], number | undefined>({
    params: () => this.#projectId() ?? undefined,
    loader: ({ params }) => firstValueFrom(this.#connectionService.getBoxApps(params!)),
  });

  readonly #oauthProvidersResource = resource<OAuthProviderConfig[], ObjectStorageProvider | undefined>({
    params: () => this.#osProvider() ?? undefined,
    loader: ({ params }) => {
      const integrationId = OAUTH_INTEGRATION_IDS[params!];
      if (integrationId === undefined) {
        return Promise.resolve([]);
      }
      return firstValueFrom(this.#oauthStorage.getProviders(integrationId)).then((providers) =>
        providers.filter((p) => p.provider.toLowerCase() === params!.toLowerCase()),
      );
    },
  });

  readonly #oauthCredentialsResource = resource<OAuthCredential[], CredentialsContext | undefined>({
    params: () => this.#credentialsContext() ?? undefined,
    loader: ({ params }) =>
      firstValueFrom(this.#oauthStorage.getCredentials(params!.projectId)).then((credentials) =>
        credentials.filter(
          (c) => !c.provider || c.provider.toUpperCase() === params!.provider.toUpperCase(),
        ),
      ),
  });

  readonly #storageGrantsResource = resource<StorageGrant[], number | undefined>({
    params: () => {
      const projectId = this.#projectId();
      return projectId == null || !this.#wantStorageGrants() ? undefined : projectId;
    },
    loader: ({ params }) => this.#storageGrants.list(params!),
  });

  // ── Public read-only views ─────────────────────────────────────────────────
  readonly secrets = this.#secretsStore.secrets;
  readonly globalServices = computed(() =>
    this.#globalServicesResource.hasValue() ? this.#globalServicesResource.value() : [],
  );
  readonly boxApps = computed(() =>
    this.#boxAppsResource.hasValue() ? this.#boxAppsResource.value() : [],
  );
  readonly oauthProviders = computed(() =>
    this.#oauthProvidersResource.hasValue() ? this.#oauthProvidersResource.value() : [],
  );
  readonly oauthCredentials = computed(() =>
    this.#oauthCredentialsResource.hasValue() ? this.#oauthCredentialsResource.value() : [],
  );

  /** Approved GCS grants a Keyless connection can use (EHQ-427). */
  readonly approvedStorageGrants = computed(() =>
    this.#storageGrantsResource.hasValue()
      ? this.#storageGrantsResource
          .value()
          .filter((g) => g.status === 'Approved' && g.provider === ObjectStorageProvider.GCS)
      : [],
  );
  readonly isLoadingStorageGrants = computed(() => this.#storageGrantsResource.isLoading());

  readonly isLoadingOAuthProviders = computed(() => this.#oauthProvidersResource.isLoading());
  readonly isLoadingOAuthCredentials = computed(() => this.#oauthCredentialsResource.isLoading());
  readonly isInitiatingOAuth = signal(false);

  /**
   * True once project-scoped init resources that always load (box apps) have
   * finished. Global services are opt-in via {@link ensureGlobalServices} and
   * must not block dialog open (EHQ-255).
   */
  readonly initDataSettled = computed(() =>
    SETTLED_STATUSES.has(this.#boxAppsResource.status()),
  );

  /** True when global services are not requested, or their load has finished. */
  readonly globalServicesSettled = computed(
    () =>
      !this.#wantGlobalServices() ||
      SETTLED_STATUSES.has(this.#globalServicesResource.status()),
  );

  /** Whether {@link ensureGlobalServices} has been called for this dialog. */
  readonly globalServicesRequested = computed(() => this.#wantGlobalServices());

  /**
   * The provider config to use when authorizing. Defaults to the first loaded
   * provider and resets when the provider list changes, but the user can
   * override it via {@link selectOAuthProviderConfig}.
   */
  readonly selectedOAuthProviderConfigId = linkedSignal<OAuthProviderConfig[], number | null>({
    source: () => this.oauthProviders(),
    computation: (providers) => providers[0]?.oauthProviderConfigId ?? null,
  });

  // ── Commands ────────────────────────────────────────────────────────────────
  /** Set the project and kick off the project-scoped init resources + secrets cache. */
  init(projectId: number): void {
    this.#projectId.set(projectId);
    this.#secretsStore.setProjectId(projectId);
  }

  /**
   * Load the global-services list for the Service connection tab.
   * Safe to call repeatedly; no-ops if already requested.
   */
  ensureGlobalServices(): void {
    this.#wantGlobalServices.set(true);
  }

  /** Load the project's storage grants for the Keyless grant picker (EHQ-427). */
  ensureStorageGrants(): void {
    this.#wantStorageGrants.set(true);
  }

  /** Load OAuth providers for the given storage provider (object-storage tab). */
  loadOAuthProvidersFor(provider: ObjectStorageProvider): void {
    this.#osProvider.set(provider);
  }

  /** Load OAuth credentials for the current project + given provider. */
  loadOAuthCredentialsFor(provider: ObjectStorageProvider): void {
    const projectId = this.#projectId();
    if (projectId == null) {
      return;
    }
    this.#credentialsContext.set({ projectId, provider });
  }

  selectOAuthProviderConfig(providerConfigId: number | null): void {
    this.selectedOAuthProviderConfigId.set(providerConfigId);
  }

  // ── Save ────────────────────────────────────────────────────────────────────
  saveStandard(
    key: StandardConnectionKey,
    isEditMode: boolean,
    connectionId: number | null,
    basics: ConnectionBasics,
    typeForm: FormValue,
  ): Observable<ConnectionSaveResult> {
    const projectId = this.#projectId()!;
    const save$ =
      isEditMode && connectionId
        ? this.#connectionService.updateConnection(
            projectId,
            connectionId,
            buildUpdateConnectionRequest(key, connectionId, basics, typeForm),
          )
        : this.#connectionService.createConnection(
            projectId,
            buildCreateConnectionRequest(key, projectId, basics, typeForm),
          );
    return save$.pipe(map((connection) => ({ type: key, connection })));
  }

  saveObjectStorage(
    isEditMode: boolean,
    connectionId: number | null,
    basics: ConnectionBasics,
    osForm: FormValue,
  ): Observable<ConnectionSaveResult> {
    const projectId = this.#projectId()!;
    const save$ =
      isEditMode && connectionId
        ? this.#objectStorageService.update(projectId, connectionId, buildObjectStorageUpdateRequest(basics, osForm))
        : this.#objectStorageService.create(projectId, buildObjectStorageCreateRequest(basics, osForm));
    return save$.pipe(map((connection) => ({ type: 'objectstorage', connection })));
  }

  // ── Test ──────────────────────────────────────────────────────────────────
  /** Test an object-storage config before saving (never throws — see ConnectionTestService). */
  testObjectStorage(name: string | null | undefined, osForm: FormValue): Observable<ConnectionTestResult> {
    return this.#connectionTest.testObjectStorage(this.#projectId()!, buildObjectStorageTestRequest(name, osForm));
  }

  // ── OAuth ───────────────────────────────────────────────────────────────────
  /**
   * Run the OAuth authorization handshake. On success reloads the credentials
   * list and returns the new credential id. A provider denial returns an
   * `error` (so the dialog can surface it), a closed popup returns
   * `cancelled`, and a blocked popup rejects.
   */
  async authorize(providerConfigId: number, displayName: string): Promise<AuthorizeResult> {
    const projectId = this.#projectId()!;
    this.isInitiatingOAuth.set(true);
    try {
      const { authorizationUrl } = await firstValueFrom(
        this.#oauthStorage.initiateOAuth(providerConfigId, {
          projectId,
          displayName: displayName || 'OAuth Credential',
        }),
      );
      this.isInitiatingOAuth.set(false);
      const message = await this.#oauthFlow.open(authorizationUrl);
      if (message.success && message.credentialId) {
        this.#oauthCredentialsResource.reload();
        return { credentialId: message.credentialId };
      }
      return { credentialId: null, error: message.error ?? 'Unknown error' };
    } catch (error) {
      this.isInitiatingOAuth.set(false);
      if (error instanceof OAuthPopupClosedError) {
        return { credentialId: null, cancelled: true };
      }
      throw error;
    }
  }
}
